import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "remainingPoints", "poolValue"]
  static values = {
    maxPoints: Number
  }

  poolValue(primary, halfAttribute, quarterAttribute) {
    return primary + Math.floor(halfAttribute / 2) + Math.floor(quarterAttribute / 4)
  }

  connect() {
    this.initializeInputs()
    this.initializeTabs()
    this.initializePoolCalculations()
    // Calculate initial pool values on page load
    this.updatePoolValues()
  }

  initializeInputs() {
    this.inputTargets.forEach(input => {
      input.addEventListener('input', (e) => this.handleSkillChange(e))
    })
    this.updatePoints()
  }

  initializeTabs() {
    // Initialize Flowbite tabs
    const tabElements = document.querySelectorAll('[data-tabs-toggle]')
    tabElements.forEach(tabs => {
      const tabButtons = tabs.querySelectorAll('[role="tab"]')
      tabButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const targetId = button.getAttribute('data-tabs-target')
          const target = document.querySelector(targetId)
          
          // Hide all panels
          document.querySelectorAll('[role="tabpanel"]').forEach(panel => {
            panel.classList.add('hidden')
          })
          
          // Show target panel
          target.classList.remove('hidden')
          
          // Update button states
          tabButtons.forEach(btn => {
            btn.setAttribute('aria-selected', 'false')
            btn.classList.remove('active')
          })
          button.setAttribute('aria-selected', 'true')
          button.classList.add('active')
        })
      })
    })
  }

  initializePoolCalculations() {
    // Find the attribute selection controller
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (attributeController) {
      // Watch for changes to any attribute input
      attributeController.querySelectorAll('[data-attribute-selection-target="input"]').forEach(input => {
        input.addEventListener('input', () => this.updatePoolValues())
        input.addEventListener('change', () => this.updatePoolValues())
      })
      // Also listen for custom events from attribute controller when pools update
      attributeController.addEventListener('poolsUpdated', () => this.updatePoolValues())
      // Initial calculation
      this.updatePoolValues()
    }
  }

  updatePoolValues() {
    const attributeController = document.querySelector('[data-controller="attribute-selection"]')
    if (!attributeController) return

    const getAttributeValue = (name) => {
      const input = attributeController.querySelector(`input[name="${name}"]`)
      return input ? parseInt(input.value, 10) || 0 : 0
    }

    // Get base attribute values
    const baseStrength = getAttributeValue('character[strength]')
    const baseBody = getAttributeValue('character[body]')
    const baseReaction = getAttributeValue('character[reaction]')
    const baseIntelligence = getAttributeValue('character[intelligence]')
    const baseWillpower = getAttributeValue('character[willpower]')
    const baseCharisma = getAttributeValue('character[charisma]')

    const heritageMods = this.getAttributeModifications(attributeController, 'heritage')
    const magicMods = this.getAttributeModifications(attributeController, 'magic')

    // Apply creation-time modifications to get effective values
    const strength = baseStrength + (heritageMods.strength || 0) + (magicMods.strength || 0)
    const body = baseBody + (heritageMods.body || 0) + (magicMods.body || 0)
    const reaction = baseReaction + (heritageMods.reaction || 0) + (magicMods.reaction || 0)
    const intelligence = baseIntelligence + (heritageMods.intelligence || 0) + (magicMods.intelligence || 0)
    const willpower = baseWillpower + (heritageMods.willpower || 0) + (magicMods.willpower || 0)
    const charisma = baseCharisma + (heritageMods.charisma || 0) + (magicMods.charisma || 0)

    // Calculate pool values using effective (final) attribute values
    const pools = {
      brawn: this.poolValue(strength, body, willpower),
      finesse: this.poolValue(reaction, body, intelligence),
      focus: this.poolValue(intelligence, reaction, willpower),
      resolve: willpower + Math.floor(intelligence / 2) + Math.floor(charisma / 2)
    }

    // Update pool values in tab headers
    this.poolValueTargets.forEach(target => {
      const poolType = target.getAttribute('data-pool-type')
      if (pools[poolType]) {
        target.textContent = pools[poolType]
      }
    })
  }

  updatePoints() {
    let totalUsedPoints = 0
    this.inputTargets.forEach(input => {
      const value = parseInt(input.value, 10) || 0
      const skillCost = this.calculateSkillCost(input, value)
      totalUsedPoints += skillCost
    })

    const remainingPoints = this.maxPointsValue - totalUsedPoints
    this.remainingPointsTarget.textContent = `Remaining Points: ${remainingPoints}`

    // Disable submit button if points are not properly allocated
    this.syncSubmitState()
  }

  getAttributeModifications(attributeController, source) {
    try {
      const modsValue = attributeController.getAttribute(`data-attribute-selection-${source}-modifications-value`)
      return modsValue ? JSON.parse(modsValue) : {}
    } catch (e) {
      console.warn(`Failed to parse ${source} modifications:`, e)
      return {}
    }
  }

  calculateSkillCost(input, value) {
    // Get the skill name from the input's name attribute
    const skillName = input.name.match(/\[([^\]]+)\]$/)?.[1] || ''
    
    // Martial arts costs 2 points per rank
    if (skillName.includes('martial_arts')) {
      return value * 2
    }
    
    // All other skills cost 1 point per rank
    return value
  }

  handleSkillChange(event) {
    const input = event.target
    const newValue = parseInt(input.value, 10) || 0
    const oldValue = parseInt(input.dataset.lastValue || "0", 10)
    
    // Calculate current total points used by other skills
    let otherSkillsTotal = 0
    this.inputTargets.forEach(skillInput => {
      if (skillInput !== input) {
        const skillValue = parseInt(skillInput.value, 10) || 0
        const skillCost = this.calculateSkillCost(skillInput, skillValue)
        otherSkillsTotal += skillCost
      }
    })

    // Calculate the cost per rank for this skill
    const costPerRank = this.calculateSkillCost(input, 1)
    
    // Calculate maximum points available for this skill
    const availablePoints = this.maxPointsValue - otherSkillsTotal
    const maxAllowedRanks = Math.floor(availablePoints / costPerRank)
    
    // Cap the value at the maximum allowed
    const cappedValue = Math.min(newValue, maxAllowedRanks, 6) // Also respect the max rank of 6
    
    // Set the capped value
    input.value = cappedValue

    // Store the new value as the last valid value
    input.dataset.lastValue = cappedValue
    
    // Update displays
    this.updatePoints()
  }

  incrementSkill(event) {
    const skillKey = event.target.closest('button').dataset.skill
    const input = document.querySelector(`input[name="character[skills][${skillKey}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 0
      const newValue = Math.min(currentValue + 1, 6)
      input.value = newValue
      
      // Trigger the input event to update calculations
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(inputEvent)
    }
  }

  decrementSkill(event) {
    const skillKey = event.target.closest('button').dataset.skill
    const input = document.querySelector(`input[name="character[skills][${skillKey}]"]`)
    if (input) {
      const currentValue = parseInt(input.value, 10) || 0
      const newValue = Math.max(currentValue - 1, 0)
      input.value = newValue
      
      // Trigger the input event to update calculations
      const inputEvent = new Event('input', { bubbles: true })
      input.dispatchEvent(inputEvent)
    }
  }

  maxPointsValueChanged() {
    this.updatePoints()
  }

  syncSubmitState() {
    const submitButton = document.querySelector('button[type="submit"]')
    if (!submitButton) return

    const remainingTargets = document.querySelectorAll(
      '[data-attribute-selection-target="remainingPoints"], [data-skill-selection-target="remainingPoints"], [data-knowledge-selection-target="remainingPoints"], [data-etiquette-selection-target="remainingPoints"]'
    )
    const hasNegativeRemaining = Array.from(remainingTargets).some((target) => {
      const match = target.textContent.match(/-?\d+/)
      return match && parseInt(match[0], 10) < 0
    })

    submitButton.disabled = hasNegativeRemaining
    submitButton.classList.toggle('opacity-50', hasNegativeRemaining)
    submitButton.classList.toggle('cursor-not-allowed', hasNegativeRemaining)
  }
}
